/* Reactive game state. Signals own truth; components subscribe.
   The cursor lives on the LEFT half of the palindrome and ranges over
   [0, cursorMax]. Typing writes the same letter at cursor and its mirror,
   then advances. Backspace deletes the letter behind the cursor.

   In Phase 1.5 the puzzle is loaded from the API on first paint, not
   picked from a bundled list. The `puzzleLoader` indirection lets
   dev mode swap in the local-YAML loader at startup; production never
   touches the dev loader (briefs/dev-mode.md §"Gate pattern").

   Module-scoped invariants:
   - `currentPuzzle` is `Puzzle | null` until `loadInitialPuzzle()` runs.
     Components must guard. Computed signals fall back to a "no puzzle
     loaded" placeholder so accessing them while the API call is in
     flight doesn't throw.
   - `gameId` is the server-issued UUID v4. Required end-to-end; the
     dev loader mints a `dev-` prefixed synthetic id so the field is
     always present on the persisted record.
   - `suppressBeacons` is set to true by the dev loader's "local puzzle"
     mode to prevent a developer playtest from polluting the analytics
     rollup with a synthetic game_id. */

import { batch, computed, signal } from "@preact/signals";
import { trackEvent } from "../analytics";
import {
  buildRevealSlots,
  changedLetterIndices,
  prefersReducedMotion,
  revealTotalMs,
} from "../effects/reveal";
import { getToday, postComplete } from "./api";
import {
  applyPlaythrough,
  buildPlaythrough,
  clearPlaythrough,
  loadPlaythrough,
  savePlaythrough,
} from "./playthrough";
import {
  type Puzzle,
  type PuzzleAnalysis,
  analyzePuzzle,
  mirrorIndex,
} from "./puzzle";
import { type Mark, aggregateKeyboard, scoreGuess } from "./scoring";

export const HINT_COST = 2;
export const SPACES_COST = 3;

export type GameStatus = "playing" | "won" | "lost";
export type GuessRow = readonly (string | null)[];

/** A loader resolves to the puzzle for the current session, the
   server-issued (or dev-synthetic) game id, and a flag that suppresses
   beacons + the /complete POST for dev playtests. */
export type PuzzleLoadResult = {
  puzzle: Puzzle;
  gameId: string;
  suppressBeacons: boolean;
};
export type PuzzleLoader = () => Promise<PuzzleLoadResult>;

/** The default loader hits the live API. Dev mode replaces this via
   `setPuzzleLoader` before `loadInitialPuzzle` runs. */
let puzzleLoader: PuzzleLoader = async () => {
  const { puzzle, gameId } = await getToday();
  return { puzzle, gameId, suppressBeacons: false };
};

export function setPuzzleLoader(loader: PuzzleLoader): void {
  puzzleLoader = loader;
}

const currentPuzzle = signal<Puzzle | null>(null);
export const puzzle = computed(() => currentPuzzle.value);

/** Set when the configured puzzle loader fails — the App keeps
   rendering so the dev panel (and a future "couldn't load" surface)
   stay reachable; the user can switch to local-puzzle mode and
   retry without a full page reload. */
export const loadError = signal<string | null>(null);

/** Server-issued UUID v4 (or a `dev-` prefixed id from the dev loader).
   Threaded into the Playthrough record and the analytics beacon. */
export const gameId = signal<string | null>(null);

/** When true, beacon events and the /complete POST are skipped — the
   dev-mode "local puzzle" path sets this so playtests don't pollute
   the production analytics rollup. */
let suppressBeacons = false;

/** Empty analysis used until the first puzzle loads. Components that
   consume `analysis.value` while the API call is in flight see this
   zero-shaped record rather than a thrown exception. The skeleton UI
   in `<BoardScroll />` masks the empty state visually. */
const EMPTY_ANALYSIS: PuzzleAnalysis = {
  sentence: "",
  normalized: "",
  letterIndices: [],
  letterCount: 0,
  pivotLetterIndices: [],
  cursorMax: 0,
};

export const analysis = computed<PuzzleAnalysis>(() =>
  currentPuzzle.value == null ? EMPTY_ANALYSIS : analyzePuzzle(currentPuzzle.value),
);

/** Format a puzzle number with at least one leading zero, padded so the
   total width is one digit wider than the natural-digit count and never
   fewer than three: 1 → "001", 9 → "009", 10 → "010", 100 → "0100",
   500 → "0500". Used in the toolbar status line and the share-grid
   header so the puzzle id reads as a stable canonical identifier. */
export function formatPuzzleNumber(n: number): string {
  const natural = String(n);
  const width = Math.max(3, natural.length + 1);
  return natural.padStart(width, "0");
}

export const puzzleNumber = computed(() =>
  currentPuzzle.value == null
    ? "000"
    : formatPuzzleNumber(currentPuzzle.value.number),
);

const blankBuffer = (): (string | null)[] =>
  new Array(analysis.value.letterCount).fill(null);

export const guesses = signal<GuessRow[]>([]);
export const buffer = signal<(string | null)[]>([]);
export const cursor = signal<number>(0);
/** Final-board-layout slot the hint occupies — the slot the player was
   about to enter at purchase, shifted past any earlier spaces purchase
   so reveals stack in the order they were bought. The two paid rows
   occupy that slot and the next, pushing later guesses down so a row
   stays in the visual line where it was typed. null means the hint has
   not been purchased. */
export const hintPurchasedAt = signal<number | null>(null);
export const hintRevealed = computed(() => hintPurchasedAt.value != null);
/** Final-board-layout slot the spaces reveal occupies — same accounting
   as `hintPurchasedAt`, shifted past an earlier hint purchase if any.
   The three paid rows occupy that slot and the next two. null means
   spaces has not been purchased. */
export const spacesPurchasedAt = signal<number | null>(null);
export const spacesPurchased = computed(() => spacesPurchasedAt.value != null);
export const status = signal<GameStatus>("playing");

/** True when the player solved the puzzle without buying either reveal —
   the "Hard Mode" boost. Surfaces both in the GameOver panel title
   ("Solved in Hard Mode!") and the share-grid footer (" • HARD MODE
   SOLVE! 🐲" appended after the spaces/hint parts). */
export const hardModeSolve = computed(
  () =>
    status.value === "won" &&
    !hintRevealed.value &&
    !spacesPurchased.value,
);

/** Increments each time submit() rejects the current buffer (empty first
   guess, or a guess identical to the immediately-preceding one). The
   active row component watches the value and triggers a horizontal-shake
   animation on the change. Monotonic counter rather than a boolean so two
   rejections in a row each fire their own animation. */
export const shakeNonce = signal(0);

export const guessesUsed = computed(
  () =>
    guesses.value.length +
    (hintRevealed.value ? HINT_COST : 0) +
    (spacesPurchased.value ? SPACES_COST : 0),
);
export const guessesRemaining = computed(() => {
  const p = puzzle.value;
  if (p == null) return 0;
  return p.maxGuesses - guessesUsed.value;
});

/** A reveal can be bought as long as it leaves the player with at least one
   guess to attempt afterward — i.e., remaining > COST. With HINT_COST=2 the
   hint stays buyable down to 3 remaining (after paying: 1 attempt left); with
   SPACES_COST=3 spaces stays buyable down to 4 remaining. The reveals are
   independent — buying hint never gates buying spaces (and vice versa) so
   long as the residual budget rule holds for each. */
export const canBuyHint = computed(
  () =>
    status.value === "playing" &&
    !hintRevealed.value &&
    guessesRemaining.value > HINT_COST,
);

export const canBuySpaces = computed(
  () =>
    status.value === "playing" &&
    !spacesPurchased.value &&
    guessesRemaining.value > SPACES_COST,
);

export const marksByGuess = computed<Mark[][]>(() => {
  if (currentPuzzle.value == null) return [];
  const sol = analysis.value.normalized.split("");
  return guesses.value.map((g) => scoreGuess(g, sol));
});

export const keyboardMarks = computed(() =>
  aggregateKeyboard(guesses.value, marksByGuess.value),
);

/** Click-the-masthead trigger: every increment runs the full win-celebration
   sequence (mouth fire-breath + viewport confetti) without touching game
   state. Useful for previewing the effects and as a small easter egg. */
export const celebrationTrigger = signal(0);
export function fireCelebration(): void {
  celebrationTrigger.value = celebrationTrigger.value + 1;
}

/** Index into `guesses` of the row currently running its outside-in
   flip-reveal animation, or null. `submit()` sets this when a guess is
   appended; `endReveal()` clears it once the per-cell flip has settled.
   While set:
     • The active row below the just-submitted row is not rendered.
     • Letter input, cursor moves, hint purchase, and resubmission are
       all gated off — the player can't enter their next guess until the
       reveal is in.
     • The status flip (win/lose) is also deferred to `endReveal()` so
       the end-screen and celebration land together, after the reveal,
       not on top of it. */
export const revealingGuessIndex = signal<number | null>(null);
export const isRevealing = computed(() => revealingGuessIndex.value != null);

/** Letter indices that turned green for the first time on the most-recent
   submitted guess. Empty when no guesses have been submitted, or when the
   latest guess didn't reveal any new greens. The "celebration trigger" for
   per-cell confetti — emit a fresh array on every submit so subscribers
   see a value change even if the indices happen to match. */
export const newGreensInLastGuess = computed<readonly number[]>(() => {
  const all = marksByGuess.value;
  if (all.length === 0) return [];
  const last = all[all.length - 1]!;
  const out: number[] = [];
  for (let i = 0; i < last.length; i++) {
    if (last[i] !== "green") continue;
    let wasAlreadyGreen = false;
    for (let g = 0; g < all.length - 1; g++) {
      if (all[g]![i] === "green") {
        wasAlreadyGreen = true;
        break;
      }
    }
    if (!wasAlreadyGreen) out.push(i);
  }
  return out;
});

/** Single source of truth for the status summary shown both below the
   reveal-buttons row and at the bottom of the Share Grid. Already
   uppercase so callers can render it verbatim. Order is `<count> •
   HINT_PART • SPACES_PART` per briefs/scoring.md (HINT before SPACES on
   the page status line; the share-grid footer uses a different terser
   form built directly in `share.ts`).

     X OF Y GUESSES LEFT • HINT UNUSED • SPACES UNUSED              (playing)
     X OF Y GUESSES LEFT • HINT USED AT N • SPACES UNUSED           (playing, hint)
     X OF Y GUESSES LEFT • HINT UNUSED • SPACES USED AT M           (playing, spaces)
     X OF Y GUESSES LEFT • HINT USED AT N • SPACES USED AT M        (playing, both)
     SOLVED IN X OF Y GUESSES • HINT UNUSED • SPACES UNUSED         (won)
     SOLVED IN X OF Y GUESSES • HINT USED AT N • SPACES UNUSED      (won, hint)
     SOLVED IN X OF Y GUESSES • HINT UNUSED • SPACES USED AT M      (won, spaces)
     SOLVED IN X OF Y GUESSES • HINT USED AT N • SPACES USED AT M   (won, both)
     UNSOLVED IN Y GUESSES • HINT UNUSED • SPACES UNUSED            (lost)
     UNSOLVED IN Y GUESSES • HINT USED AT N • SPACES UNUSED         (lost, hint)
     UNSOLVED IN Y GUESSES • HINT UNUSED • SPACES USED AT M         (lost, spaces)
     UNSOLVED IN Y GUESSES • HINT USED AT N • SPACES USED AT M      (lost, both)

   N and M are 1-based: the final-board-layout slot the player was
   entering at purchase. */
export const statusLine = computed(() => {
  const p = puzzle.value;
  if (p == null) return "";
  const total = p.maxGuesses;
  const used = guessesUsed.value;
  const remaining = guessesRemaining.value;
  const hintAt = hintPurchasedAt.value;
  const spacesAt = spacesPurchasedAt.value;
  const hintPart =
    hintAt == null ? "HINT UNUSED" : `HINT USED AT ${hintAt + 1}`;
  const spacesPart =
    spacesAt == null ? "SPACES UNUSED" : `SPACES USED AT ${spacesAt + 1}`;
  let countPart: string;
  if (status.value === "won") {
    countPart = `SOLVED IN ${used} OF ${total} GUESSES`;
  } else if (status.value === "lost") {
    countPart = `UNSOLVED IN ${total} GUESSES`;
  } else {
    countPart = `${remaining} OF ${total} GUESSES LEFT`;
  }
  return `${countPart} • ${hintPart} • ${spacesPart}`;
});

/** Tracks whether the `Puzzle Started` beacon has fired for the current
   game. Reset by `installPuzzle`. */
let startedFired = false;

export function typeLetter(rawCh: string): void {
  if (status.value !== "playing") return;
  if (isRevealing.value) return;
  const p = puzzle.value;
  if (p == null) return;
  const ch = rawCh.toLowerCase();
  if (ch.length !== 1 || !/[a-z]/.test(ch)) return;

  const a = analysis.value;
  const buf = buffer.value.slice();
  const cur = cursor.value;
  const mirror = mirrorIndex(cur, a.letterCount);
  buf[cur] = ch;
  buf[mirror] = ch;
  buffer.value = buf;
  // Advance the cursor onto the next empty left-half slot, skipping
  // already-filled positions (typically carry-forward greens). At
  // cursorMax — or with no empty slot to the right — stay put so the
  // last letter typed can be replaced in place.
  if (cur < a.cursorMax) {
    let next = cur + 1;
    while (next <= a.cursorMax && buf[next] != null) next++;
    if (next <= a.cursorMax) cursor.value = next;
  }

  // First valid letter typed in this game → fire `Puzzle Started`.
  // Suppressed in the dev local-puzzle path so playtests don't show
  // up in Cloudflare's rollups.
  if (!startedFired && !suppressBeacons) {
    startedFired = true;
    const id = gameId.value;
    if (id != null) {
      trackEvent("Puzzle Started", {
        game_id: id,
        number: p.number,
        date: p.date,
      });
    }
  }
}

export function backspace(): void {
  if (status.value !== "playing") return;
  if (isRevealing.value) return;
  if (currentPuzzle.value == null) return;
  const a = analysis.value;
  const cur = cursor.value;
  const buf = buffer.value.slice();

  if (buf[cur] != null) {
    // Clear current pair without moving cursor (allows in-place replacement).
    buf[cur] = null;
    buf[mirrorIndex(cur, a.letterCount)] = null;
    buffer.value = buf;
    return;
  }
  // Already empty — step left, then clear.
  if (cur > 0) {
    const next = cur - 1;
    buf[next] = null;
    buf[mirrorIndex(next, a.letterCount)] = null;
    buffer.value = buf;
    cursor.value = next;
  }
}

export function moveCursor(dir: -1 | 1): void {
  if (status.value !== "playing") return;
  if (isRevealing.value) return;
  if (currentPuzzle.value == null) return;
  const cur = cursor.value;
  const next = cur + dir;
  if (next < 0 || next > analysis.value.cursorMax) return;
  cursor.value = next;
}

/** Pending end-of-reveal timer. Tracked so swap/reset can cancel it
   before the deferred carry-forward runs against a fresh game. */
let revealTimer: number | null = null;
function cancelRevealTimer(): void {
  if (revealTimer != null) {
    clearTimeout(revealTimer);
    revealTimer = null;
  }
}

export function submit(): void {
  if (status.value !== "playing") return;
  if (isRevealing.value) return;
  if (currentPuzzle.value == null) return;
  const guess: GuessRow = buffer.value.slice();
  const previous =
    guesses.value.length > 0
      ? guesses.value[guesses.value.length - 1]!
      : null;
  const previousMarks =
    guesses.value.length > 0
      ? marksByGuess.value[guesses.value.length - 1]!
      : null;
  // Reject submissions that would waste a guess with no signal:
  //   - an entirely empty first guess (no letters typed at all);
  //   - a row identical to the immediately-preceding guess after
  //     reducing that previous guess to just its greens. Only
  //     greens carry forward into the next buffer; yellows and
  //     grays go back to empty. So the natural "what would the
  //     buffer look like if I submitted right now without typing"
  //     is the green-only projection of the previous guess. If
  //     the current buffer matches that, the submit adds no new
  //     information.
  // Subsequent empty rows ARE allowed — the player can deliberately
  // skip to check fewer letters. Only the very first guess is
  // protected from a no-op submit.
  if (previous == null && guess.every((c) => c == null)) {
    shakeNonce.value++;
    return;
  }
  if (previous != null && previousMarks != null) {
    const sameAsPrevious = guess.every((c, i) => {
      const prevCell =
        previousMarks[i] === "green" ? (previous[i] ?? null) : null;
      return c === prevCell;
    });
    if (sameAsPrevious) {
      shakeNonce.value++;
      return;
    }
  }
  guesses.value = [...guesses.value, guess];
  const idx = guesses.value.length - 1;
  revealingGuessIndex.value = idx;

  // Schedule the end-of-reveal commit. Until it fires, the active row
  // is hidden, input is gated, and status stays "playing" — so the
  // end-screen and celebration land together after the reveal, not on
  // top of it. Reduced-motion users skip the wait.
  const slotCount = buildRevealSlots(
    changedLetterIndices(guess, previous),
    analysis.value.letterCount,
  ).size;
  const wait = prefersReducedMotion() ? 0 : revealTotalMs(slotCount);
  cancelRevealTimer();
  if (wait <= 0) {
    endReveal();
  } else {
    revealTimer = window.setTimeout(() => {
      revealTimer = null;
      endReveal();
    }, wait);
  }
}

/** Apply the deferred consequences of the just-submitted guess: flip
   status to won/lost if applicable, otherwise carry forward greens
   into the next buffer and park the cursor on the first empty
   left-half slot. Always clears `revealingGuessIndex` so the active
   row appears and input resumes. Idempotent — a no-op when there's no
   pending reveal. Tests call this synchronously to skip the timer. */
export function endReveal(): void {
  if (revealingGuessIndex.value == null) return;
  cancelRevealTimer();

  const idx = revealingGuessIndex.value;
  const guess = guesses.value[idx];
  if (guess == null) {
    // Guesses got cleared (reset/swap) under us — just clear the flag.
    revealingGuessIndex.value = null;
    return;
  }

  const allFilled = guess.every((c) => c != null);
  if (allFilled && guess.join("") === analysis.value.normalized) {
    status.value = "won";
    revealingGuessIndex.value = null;
    persistGame();
    return;
  }
  if (guessesRemaining.value === 0) {
    status.value = "lost";
    revealingGuessIndex.value = null;
    persistGame();
    return;
  }
  // Carry forward every position that has ever been green: those letters are
  // provably correct (palindrome ⇒ mirror is also green automatically). The
  // player can still type over them; if they choose to, that's on them.
  const next = blankBuffer();
  const allGuesses = guesses.value;
  const allMarks = marksByGuess.value;
  for (let g = 0; g < allGuesses.length; g++) {
    const gs = allGuesses[g]!;
    const ms = allMarks[g]!;
    for (let i = 0; i < gs.length; i++) {
      if (ms[i] === "green") next[i] = gs[i] ?? null;
    }
  }
  buffer.value = next;
  // Park the cursor at the first still-empty left-half slot so the player
  // doesn't have to arrow past the pre-filled greens.
  const cursorMax = analysis.value.cursorMax;
  let firstEmpty = 0;
  for (let i = 0; i <= cursorMax; i++) {
    if (next[i] == null) { firstEmpty = i; break; }
    if (i === cursorMax) firstEmpty = 0;
  }
  cursor.value = firstEmpty;
  revealingGuessIndex.value = null;
  // Non-terminal commit: snapshot the in-progress playthrough to
  // localStorage so a refresh / tab-restore lands on the
  // post-commit state instead of starting over.
  persistGame();
}

export function buyHint(): void {
  if (!canBuyHint.value) return;
  if (isRevealing.value) return;
  // Final-layout slot: guesses already submitted, plus the SPACES_COST
  // shift if spaces was bought before the latest guess. Pre-Phase-2.2
  // hint-only behavior is preserved (no spaces ⇒ shift = 0 ⇒ identical
  // value to the prior `guesses.length` write).
  const spacesShift =
    spacesPurchasedAt.value != null &&
    spacesPurchasedAt.value <= guesses.value.length
      ? SPACES_COST
      : 0;
  hintPurchasedAt.value = guesses.value.length + spacesShift;
  if (guessesRemaining.value === 0) {
    status.value = "lost";
  }
  persistGame();
}

export function buySpaces(): void {
  if (!canBuySpaces.value) return;
  if (isRevealing.value) return;
  // Mirror of `buyHint`: shift past an earlier hint purchase so each
  // reveal's slot index is its final-board-layout position.
  const hintShift =
    hintPurchasedAt.value != null &&
    hintPurchasedAt.value <= guesses.value.length
      ? HINT_COST
      : 0;
  spacesPurchasedAt.value = guesses.value.length + hintShift;
  if (guessesRemaining.value === 0) {
    status.value = "lost";
  }
  persistGame();
}

/** Snapshot the current game into a Playthrough and write it to
   localStorage (keyed by puzzle number). Always writes — both
   in-progress ("abandoned" outcome) and terminal ("won"/"lost") —
   so a refresh / tab-restore lands the player on their post-commit
   state instead of starting over. On terminal outcomes we also fire
   the `Puzzle Finished` beacon and POST to /complete; in-progress
   outcomes are local-only (no analytics noise, no server token
   redemption mid-run). */
function persistGame(): void {
  const p = currentPuzzle.value;
  const id = gameId.value;
  if (p == null || id == null) return;
  const record = buildPlaythrough({
    puzzle: p,
    gameId: id,
    analysis: analysis.value,
    guesses: guesses.value,
    hintPurchasedAt: hintPurchasedAt.value,
    spacesPurchasedAt: spacesPurchasedAt.value,
    status: status.value,
  });
  if (record == null) return;
  savePlaythrough(record);

  if (suppressBeacons) return;

  // Beacon + /complete only on terminal commit. In-progress
  // ("abandoned") records stay local — they're a refresh-recovery
  // affordance, not a server event.
  if (status.value === "won" || status.value === "lost") {
    trackEvent("Puzzle Finished", {
      game_id: id,
      number: p.number,
      date: p.date,
      outcome: status.value,
    });
    // Fire-and-forget. postComplete never throws.
    void postComplete(record);
  }
}

/** True while `tryRestorePlaythrough` is mutating signals to install a
   stored finished-game state. Reactive subscribers (BoardEffects) read
   this flag to suppress side-effects that should fire only on a *fresh*
   win — the per-cell confetti pops and the win-burst celebration.
   Live-bound import: consumers see the current value, not a snapshot. */
export let isHydrating = false;

/** If a stored playthrough exists for the current puzzle, hydrate the
   signals from it so the player resumes where they left off. Both
   terminal (read-only finished view) and in-progress (resume mid-run)
   records are restored. Caller's responsibility to invoke after the
   puzzle has been swapped in and the per-puzzle signals reset.

   Wraps the writes in `batch()` so the effects fire exactly once with
   the final state, and toggles `isHydrating` around the batch so that
   single fire happens with the flag set — letting subscribers update
   their tracking variables while skipping celebrations. */
function tryRestorePlaythrough(): void {
  const p = currentPuzzle.value;
  if (p == null) return;
  const stored = loadPlaythrough(p.number);
  if (stored == null) return;
  const applied = applyPlaythrough(stored, analysis.value);
  if (applied == null) return;
  isHydrating = true;
  try {
    batch(() => {
      guesses.value = applied.guesses;
      hintPurchasedAt.value = applied.hintPurchasedAt;
      spacesPurchasedAt.value = applied.spacesPurchasedAt;
      status.value = applied.status;
      if (applied.status === "playing") {
        // Resume mid-run: rebuild buffer + cursor from the carry-
        // forward greens of the restored guesses, mirroring the
        // post-commit state endReveal() leaves behind. The half-typed
        // current row is not preserved (only committed actions are
        // serialized), so any letters typed but not submitted before
        // the refresh are lost — the player retypes them.
        const next = blankBuffer();
        const sol = analysis.value.normalized.split("");
        for (const g of applied.guesses) {
          const m = scoreGuess(g, sol);
          for (let i = 0; i < g.length; i++) {
            if (m[i] === "green") next[i] = g[i] ?? null;
          }
        }
        buffer.value = next;
        const cursorMax = analysis.value.cursorMax;
        let firstEmpty = 0;
        for (let i = 0; i <= cursorMax; i++) {
          if (next[i] == null) { firstEmpty = i; break; }
          if (i === cursorMax) firstEmpty = 0;
        }
        cursor.value = firstEmpty;
        // The original session already fired `Puzzle Started` for
        // this puzzle (engagement was committed; we wouldn't have a
        // record otherwise). Suppress the second fire so analytics
        // see one start per player run, not one per page load.
        startedFired = true;
      }
    });
  } finally {
    isHydrating = false;
  }
}

/** Install a freshly-loaded puzzle into the signals. Resets per-game
   state, clears the started-beacon latch, and sets the suppressBeacons
   flag. The caller is responsible for any subsequent
   `tryRestorePlaythrough()` if a prior finished game exists. */
export function installPuzzle(
  p: Puzzle,
  id: string,
  opts: { suppressBeacons: boolean },
): void {
  cancelRevealTimer();
  // Order matters: clear the per-game signals BEFORE swapping the
  // puzzle so that letter-count-shaped consumers (marksByGuess) see
  // empty guesses against the new analysis, never the old.
  guesses.value = [];
  hintPurchasedAt.value = null;
  spacesPurchasedAt.value = null;
  status.value = "playing";
  revealingGuessIndex.value = null;
  currentPuzzle.value = p;
  gameId.value = id;
  buffer.value = new Array(analysis.value.letterCount).fill(null);
  cursor.value = 0;
  startedFired = false;
  suppressBeacons = opts.suppressBeacons;
  // If the player has already completed this puzzle in a prior
  // session, hydrate the signals from the stored playthrough so the
  // finished game is shown read-only.
  tryRestorePlaythrough();
}

/** Boot the game: invoke the configured loader and install the
   resulting puzzle. Called from main.tsx before the first render.
   Loader failures populate `loadError` instead of throwing so the
   App still renders and the dev panel stays reachable. */
export async function loadInitialPuzzle(): Promise<void> {
  try {
    const result = await puzzleLoader();
    loadError.value = null;
    installPuzzle(result.puzzle, result.gameId, {
      suppressBeacons: result.suppressBeacons,
    });
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : String(e);
    currentPuzzle.value = null;
  }
}

/** Re-run the configured loader. The dev panel's Reload button calls
   this so a switch between Live API and Local puzzle takes effect
   without a full page reload. Same error semantics as load.

   Always clears the localStorage playthrough for the puzzle that was
   just fetched so the dev session starts fresh — otherwise an earlier
   finished game would hydrate immediately via tryRestorePlaythrough. */
export async function reloadPuzzle(): Promise<void> {
  try {
    const result = await puzzleLoader();
    clearPlaythrough(result.puzzle.number);
    loadError.value = null;
    installPuzzle(result.puzzle, result.gameId, {
      suppressBeacons: result.suppressBeacons,
    });
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : String(e);
    currentPuzzle.value = null;
  }
}

/** Reset the current game (same puzzle). Exposed primarily for tests. */
export function reset(): void {
  cancelRevealTimer();
  guesses.value = [];
  buffer.value = new Array(analysis.value.letterCount).fill(null);
  cursor.value = 0;
  hintPurchasedAt.value = null;
  spacesPurchasedAt.value = null;
  status.value = "playing";
  revealingGuessIndex.value = null;
}

/** Force a specific puzzle. Tests use this for determinism. */
export function setPuzzle(p: Puzzle, id: string = "test-game-id"): void {
  installPuzzle(p, id, { suppressBeacons: true });
}

